import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Box,
  Tooltip,
  Button,
  Typography,
  Popper,
  Paper,
  Stack,
  FormControlLabel,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PdfViewImage from "../../assets/images/Pdf-view.png";
import Styles from "./MasterCommonTable.module.scss";
import { visuallyHidden } from "@mui/utils";
import CommonLoader from "../../sharedComponents/CommonLoader/CommonLoader";
// Import additional icons
import BugReportIcon from '../../assets/images/bug.png';
import TaskIcon from '../../assets/images/task.png';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import VerifiedIcon from '@mui/icons-material/Verified';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

import ConfirmDelete_Icon from "../../assets/images/CommonTableImg/ConfirmDelete_Icon.svg";
import Right_Icon from "../../assets/images/Right_Icon.svg";
import Wrong_Icon from "../../assets/images/Wrong_Icon.svg";
import Three_Dot from "../../assets/images/CommonTableImg/Three_Dots.svg";

import Switch from "@mui/material/Switch";
import { styled } from "@mui/material/styles";
import EmptyData_Image from "../../assets/images/CommonTableImg/EmptyData_Icon.svg";
import moment from "moment";

import {
  Dialog,
  ClickAwayListener,
} from "@mui/material";

const MasterCommonTable = ({
  columns,
  data,
  handleEdit,
  handleView,
  handleToggle,
  handleApprove,
  handleRevert,
  handleRevertToWaiting,
  handleReject,
  handleDelete,
  handleHistory,
  handleAvailable,
  tableConfig,
  tableActionButton,
  openLoader,
  tableTotalCount = 0,
  isServerSide = false,
  maxSetHeight = false,
  tableOutputEvent = () => { },
  getRowStyle,
  isPaginationBar = true,
}) => {
  const [order, setOrder] = React.useState("");
  const [orderBy, setOrderBy] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [sortedData, setSortedData] = React.useState([]);

  useEffect(() => {
    if (!isServerSide) {
      const sorted = stableSort(data, getComparator(order, orderBy));
      setSortedData(sorted);
    } else {
      setSortedData(data);
      if (data?.length === 0 && tableTotalCount) {
        setPage((prev) => (prev > 0 ? prev - 1 : 0));
        if (page >= 0) {
          tableOutputEvent({
            page: page || 1,
            limit: rowsPerPage,
            sortType: order,
            sort: order === "desc" ? `-${orderBy}` : orderBy,
          });
        }
      }
    }
  }, [data, order, orderBy, isServerSide]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [isOptionsVisible, setIsOptionsVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);

  const toggleOptionsVisibility = (event, row) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    setIsOptionsVisible((prev) => !prev);
    setSelectedRow(row);
  };

  const closeOptions = () => {
    setIsOptionsVisible(false);
    setAnchorEl(null);
  };

  const IOSSwitch = styled((props) => (
    <Switch
      focusVisibleClassName=".Mui-focusVisible"
      disableRipple
      {...props}
    />
  ))(({ theme }) => ({
    width: 35,
    height: 17,
    padding: 0,
    "& .MuiSwitch-switchBase": {
      padding: "0px",
      margin: 1,
      transitionDuration: "300ms",
      "&.Mui-checked": {
        transform: "translateX(18px)",
        color: "#fff",
        "& + .MuiSwitch-track": {
          backgroundColor: "#008080",
          opacity: 1,
          border: 0,
          ...theme.applyStyles("dark", {
            backgroundColor: "#2ECA45",
          }),
        },
        "&.Mui-disabled + .MuiSwitch-track": {
          opacity: 0.5,
        },
      },
      "&.Mui-focusVisible .MuiSwitch-thumb": {
        color: "#33cf4d",
        border: "6px solid #fff",
      },
      "&.Mui-disabled .MuiSwitch-thumb": {
        color: theme.palette.grey[100],
        ...theme.applyStyles("dark", {
          color: theme.palette.grey[600],
        }),
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.7,
        ...theme.applyStyles("dark", {
          opacity: 0.3,
        }),
      },
    },
    "& .MuiSwitch-thumb": {
      boxSizing: "border-box",
      width: 15,
      height: 15,
    },
    "& .MuiSwitch-track": {
      borderRadius: 25 / 2,
      backgroundColor: "#676767",
      opacity: 1,
      transition: theme.transitions.create(["background-color"], {
        duration: 500,
      }),
      ...theme.applyStyles("dark", {
        backgroundColor: "#39393D",
      }),
    },
  }));

  const handleRequestSort = (event, property) => {
    setPage(0);
    const isAsc = orderBy === property && order === "asc";
    const orderType = isAsc ? "desc" : "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    if (isServerSide) {
      setSortedData(data);
      tableOutputEvent({
        page: 1,
        limit: rowsPerPage,
        sortType: orderType,
        sort: orderType === "desc" ? `-${property}` : property,
      });
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (isServerSide) {
      tableOutputEvent({
        page: newPage + 1,
        limit: rowsPerPage,
        sortType: order,
        sort: order === "desc" ? `-${orderBy}` : orderBy,
      });
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    if (isServerSide) {
      tableOutputEvent({
        page: 1,
        limit: parseInt(event.target.value, 10),
        sortType: order,
        sort: order === "desc" ? `-${orderBy}` : orderBy,
      });
    }
  };

  const stableSort = (array, comparator) => {
    if (!array || !Array.isArray(array)) {
      return [];
    }
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  const getComparator = (order, orderBy) => {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  const RenderView = (value, columnId, row) => {
    // Handle null/undefined values
    if (value == null) return "---";

    // Handle array values
    if (Array.isArray(value)) {
      const joinedValues = value.filter(v => v != null).join(", ");
      return (
        <Typography className={Styles.TableBodyCell}>
          {joinedValues || "---"}
        </Typography>
      );
    }

    // Custom rendering based on column ID
    switch (columnId) {

      case 'status':
        let statusIcon = null;
        let statusColor = '';

        switch (value) {
          case 'To Do':
            statusIcon = <RadioButtonUncheckedIcon fontSize="small" />;
            statusColor = '#9CA3AF';
            break;
          case 'InProgress':
            statusIcon = <AutorenewIcon fontSize="small" />;
            statusColor = '#3B82F6';
            break;
          case 'QA':
            statusIcon = <VerifiedIcon fontSize="small" />;
            statusColor = '#8B5CF6';
            break;
          case 'Completed':
            statusIcon = <CheckCircleIcon fontSize="small" />;
            statusColor = '#10B981';
            break;
          case 'Production':
            statusIcon = <RocketLaunchIcon fontSize="small" />;
            statusColor = '#F59E0B';
            break;
          default:
            break;
        }



      case 'priority':
        let priorityIcon = null;
        let priorityColor = '';

        switch (value) {
          case 'High':
            // priorityIcon = <ArrowUpwardIcon fontSize="small" />;
            priorityColor = '#F30E0E';
            break;
          case 'Medium':
            // priorityIcon = <HorizontalRuleIcon fontSize="small" />;
            priorityColor = '#1087EF';
            break;
          case 'Low':
            // priorityIcon = <ArrowDownwardIcon fontSize="small" />;
            priorityColor = '#009735';
            break;
          default:
            break;
        }

        return (
          <Box display="flex" alignItems="center" gap={1}>
            {priorityIcon}
            <span style={{ color: priorityColor }}>{value}</span>
          </Box>
        );

      case 'taskType':
        let typeIcon = null;
        let typeColor = 'black';

        switch (value) {
          case 'Bugs':
            typeIcon = <img src={BugReportIcon} style={{ width: "18px", height: "18px" }} />;

            break;
          case 'Task':
            typeIcon = <img src={TaskIcon} style={{ width: "18px", height: "18px" }} />

            break;
          default:
            break;
        }

        return (
          <Box display="flex" alignItems="center" gap={1}>
            {typeIcon}
            <span style={{ color: typeColor }}>{value}</span>
          </Box>
        );

      case 'deadlineDate':
      case 'deadLineDate':
        const isOverdue = moment(row.deadLineDate).isBefore(moment(), 'day');
        return (
          <Tooltip title={isOverdue ? "Overdue task!" : ""} arrow>
            <span style={{ color: isOverdue ? '#EF4444' : 'inherit' }}>
              {moment(row?.deadLineDate).format("DD/MM/YYYY")}
            </span>
          </Tooltip>
        );

      case 'timeDifference':
        // Show red if difference is over 15 minutes
        const isHighDifference = row.timeDifferenceMinutes > 15;
        return (
          <Tooltip title={isHighDifference ? "Difference exceeds 15 minutes!" : ""} arrow>
            <span style={{
              color: isHighDifference ? '#EF4444' : '#10B981',
              fontWeight: isHighDifference ? 'bold' : 'normal'
            }}>
              {value}
            </span>
          </Tooltip>
        );

      case 'workingTime':
        return (
          <Box className={Styles.TableBodyCell}>
            <span>{value}</span>
            {row?.pendingNoteMinutes > 0 && (
              <span style={{ color: "#b45309", fontSize: 11, display: "block", fontWeight: 500 }}>
                +{row.pendingNoteMinutes}m pending
              </span>
            )}
            {row?.approvedNoteMinutes > 0 && (
              <span style={{ color: "#15803d", fontSize: 11, display: "block", fontWeight: 500 }}>
                incl. {row.approvedNoteMinutes}m approved
              </span>
            )}
          </Box>
        );

      default:
        return (
          <Typography className={Styles.TableBodyCell}>
            {value}
          </Typography>
        );
    }

  };

  const [activeAction, setActiveAction] = useState(null);

  return (
    <div className={Styles.renderingTable}>
      <TableContainer className={Styles.tableContainer}>
        {openLoader ? (
          <Box className={Styles.loaderContainer}>{<CommonLoader />}</Box>
        ) : (
          <Box sx={{ height: "100%" }}>
            <Box
              sx={{ height: "100%" }}
              className={`${maxSetHeight === true
                ? Styles.tableContent2
                : Styles.tableContent
                } no-scrollbar`}
            >
              <Table stickyHeader className={sortedData?.length ? "" : "h-100"}>
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        sortDirection={orderBy === column.id ? order : false}
                        className={Styles.TableCell}
                        align="left"
                      >
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : "asc"}
                          onClick={(e) => handleRequestSort(e, column.id)}
                        >
                          {column.label}
                          {orderBy === column.id ? (
                            <Box component="span" sx={visuallyHidden}>
                              {order === "desc"
                                ? "sorted descending"
                                : "sorted ascending"}
                            </Box>
                          ) : null}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    {tableConfig.isAction ? (
                      <TableCell className={Styles.TableCell} align="center">
                        Action
                      </TableCell>
                    ) : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedData?.length ? (
                    (isServerSide || !isPaginationBar
                      ? sortedData
                      : sortedData.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                    ).map((row, index) => {
                      return (
                        <TableRow
                          hover
                          key={index}
                          sx={{
                            backgroundColor: 'white',
                            '&:hover': {
                              backgroundColor: row.status === 'Completed' ? '#f0fff0' :
                                row.status === 'InProgress' ? '#f0f8ff' :
                                  row.status === 'QA' ? '#f5f0ff' : 'white'
                            },
                            ... (getRowStyle ? getRowStyle(row) : {})
                          }}
                        >
                          {columns.map((column) => (
                            <TableCell
                              key={column.id}
                              className={Styles.TableBodyCell}
                              onClick={() => {
                                if (tableConfig.isView) {
                                  handleView(row);
                                  closeOptions();
                                }
                              }}
                            >
                              {RenderView(row[column.id], column.id, row)}
                            </TableCell>
                          ))}
                          {tableConfig.isAction ? (
                            <TableCell className={Styles.TableBodyCell}>
                              <Box className={Styles.ActionContainer}>
                                {(row.status === "Pending" || row.status === "Rejected") && (
                                  <Stack
                                    sx={{
                                      display: tableConfig.isApprove
                                        ? "block"
                                        : "none",
                                      lineHeight: 0,
                                    }}
                                  >
                                    <Tooltip title="Approve">
                                      <img
                                        src={Right_Icon}
                                        alt="Right-icon"
                                        onClick={() => handleApprove(row)}
                                        style={{
                                          display: "inline",
                                          color: "green",
                                        }}
                                      />
                                    </Tooltip>
                                  </Stack>
                                )}
                                {row.status === "Pending" && (
                                  <Stack
                                    sx={{
                                      display: tableConfig.isReject
                                        ? "block"
                                        : "none",
                                      lineHeight: 0,
                                    }}
                                  >
                                    <Tooltip title="Reject">
                                      <img
                                        src={Wrong_Icon}
                                        alt="Wrong-icon"
                                        onClick={() => handleReject(row)}
                                        style={{
                                          display: "inline",
                                          color: "red",
                                        }}
                                      />
                                    </Tooltip>
                                  </Stack>
                                )}
                                <Box
                                  sx={{
                                    display: tableConfig.isPdfView
                                      ? "block"
                                      : "none",
                                  }}
                                >
                                  <Tooltip title="View">
                                    <button
                                      onClick={() => handleView(row)}
                                      style={{
                                        background: "none",
                                        border: "none",
                                        padding: 0,
                                        cursor: "pointer",
                                      }}
                                    >
                                      <img
                                        src={PdfViewImage}
                                        alt="pdfView-icon"
                                        style={{ display: "inline" }}
                                      />
                                    </button>
                                  </Tooltip>
                                </Box>
                                <Box
                                  sx={{
                                    display: tableConfig.isToggle
                                      ? "block"
                                      : "none",
                                  }}
                                >
                                  <Tooltip title="Active">
                                    <button
                                      onClick={() => handleToggle(row)}
                                      style={{
                                        background: "none",
                                        border: "none",
                                        padding: 0,
                                        cursor: "pointer",
                                        color: "red",
                                      }}
                                    >
                                      <FormControlLabel
                                        control={
                                          <IOSSwitch
                                            checked={row.isEnabled}
                                          />
                                        }
                                      />
                                    </button>
                                  </Tooltip>
                                </Box>

                                {row?.status != "Reverted" &&
                                  row?.status != "InProgress" &&
                                  row?.status != "Rejected" && (
                                    <Box
                                      sx={{
                                        display:
                                          (tableConfig.isMore &&
                                            tableConfig.isClient) ||
                                            tableConfig.isAdmin ||
                                            (!tableConfig.isAdmin &&
                                              row?.status == "Pending")
                                            ? "block"
                                            : "none",
                                      }}
                                    >
                                      <Stack
                                        onClick={(event) =>
                                          toggleOptionsVisibility(event, row)
                                        }
                                        onClose={() => closeOptions()}
                                        style={{
                                          background: "none",
                                          border: "none",
                                          padding: 0,
                                          cursor: "pointer",
                                        }}
                                      >
                                        {/* <HiOutlinePencil
                                          size={32}
                                          sx={{ color: "red" }}
                                        /> */}
                                        <img src={Three_Dot} alt="" />
                                      </Stack>
                                    </Box>
                                  )}

                                {tableActionButton &&
                                  tableActionButton.map((button, btnIndex) => (
                                    <Button
                                      key={btnIndex}
                                      onClick={() => button.onClick(row)}
                                      className={Styles.actionButton}
                                    >
                                      {button.title}
                                    </Button>
                                  ))}
                              </Box>
                            </TableCell>
                          ) : null}
                          {isOptionsVisible && selectedRow === row && (
                            <>
                              <ClickAwayListener onClickAway={closeOptions}>
                                <Popper
                                  open={isOptionsVisible}
                                  anchorEl={anchorEl}
                                  placement="bottom-end"
                                  style={{
                                    zIndex: 999,
                                    boxShadow: "none",
                                    border: "none",
                                  }}
                                >
                                  <Paper
                                    sx={{
                                      display: "flex",
                                      flexDirection: "row",
                                      alignItems: "center",
                                      alignContent: "center",
                                      backgroundColor: "white",
                                      border: "none",
                                      padding: 1,
                                    }}
                                  >
                                    <Stack spacing={0.5}>
                                      {tableConfig.isEdit && (
                                        <Stack
                                          spacing={0.5}
                                          direction={"row"}
                                          onClick={() => {
                                            handleEdit(selectedRow);
                                            closeOptions();
                                          }}
                                          sx={{
                                            cursor: "pointer",
                                            color: "#3D3D3D",
                                            display: "flex",
                                            "&:hover": {
                                              color: "#008080",
                                            },
                                            alignItems: "center",
                                          }}
                                        >
                                          <Typography>Edit</Typography>
                                        </Stack>
                                      )}
                                      {tableConfig.isHistory && (
                                        <Stack
                                          spacing={0.5}
                                          direction={"row"}
                                          onClick={() => {
                                            handleHistory(selectedRow);
                                            closeOptions();
                                          }}
                                          sx={{
                                            cursor: "pointer",
                                            color: "#3D3D3D",
                                            display: "flex",
                                            "&:hover": {
                                              color: "#008080",
                                            },
                                            alignItems: "center",
                                          }}
                                        >
                                          <Typography>History</Typography>
                                        </Stack>
                                      )}

                                      {tableConfig.isAvailableLeave && (
                                        <Stack
                                          spacing={0.5}
                                          direction={"row"}
                                          onClick={() => {
                                            handleAvailable(selectedRow);
                                            closeOptions();
                                          }}
                                          sx={{
                                            cursor: "pointer",
                                            color: "#3D3D3D",
                                            display: "flex",
                                            "&:hover": {
                                              color: "#008080",
                                            },
                                            alignItems: "center",
                                          }}
                                        >
                                          <Typography>
                                            Available Leave
                                          </Typography>
                                        </Stack>
                                      )}
                                      {row?.isReverted && (
                                        <Stack
                                          onClick={() => {
                                            handleRevert(selectedRow);
                                            closeOptions();
                                          }}
                                          sx={{
                                            cursor: "pointer",
                                            color: "#3D3D3D",
                                            display: "flex",
                                            "&:hover": {
                                              color: "#008080",
                                            },
                                          }}
                                        >
                                          <Typography>Revert </Typography>
                                        </Stack>
                                      )}
                                      {row?.status === "Pending" && (
                                        <Stack
                                          onClick={() => {
                                            setSelectedRow(selectedRow);
                                            setOpenPopup(true);
                                            closeOptions();
                                          }}
                                          sx={{
                                            cursor: "pointer",
                                            color: "#3D3D3D",
                                            display: "flex",
                                            "&:hover": {
                                              color: "#008080",
                                            },
                                          }}
                                        >
                                          <Typography>Delete</Typography>
                                        </Stack>
                                      )}
                                      {row?.status === "To Do" && (
                                        <Stack
                                          onClick={() => {
                                            setSelectedRow(selectedRow);
                                            setOpenPopup(true);
                                            closeOptions();
                                          }}
                                          sx={{
                                            cursor: "pointer",
                                            color: "#3D3D3D",
                                            display: "flex",
                                            "&:hover": {
                                              color: "#008080",
                                            },
                                          }}
                                        >
                                          <Typography>Delete</Typography>
                                        </Stack>
                                      )}
                                      {tableConfig.isDelete && (
                                        <Stack
                                          onClick={() => {
                                            setSelectedRow(selectedRow);
                                            setOpenPopup(true);
                                            closeOptions();
                                          }}
                                          sx={{
                                            cursor: "pointer",
                                            color: "#3D3D3D",
                                            display: "flex",
                                            "&:hover": {
                                              color: "#008080",
                                            },
                                          }}
                                        >
                                          <Typography>Delete</Typography>
                                        </Stack>
                                      )}
                                      {tableConfig.isApprove && selectedRow?.status === "Waiting" && (
                                        <Stack
                                          onClick={() => {
                                            handleApprove(selectedRow);
                                            closeOptions();
                                          }}
                                          sx={{
                                            cursor: "pointer",
                                            color: "#3D3D3D",
                                            display: "flex",
                                            "&:hover": {
                                              color: "#008080",
                                            },
                                          }}
                                        >
                                          <Typography>Approve</Typography>
                                        </Stack>
                                      )}
                                      {tableConfig.isRevert && selectedRow?.status === "Success" && (
                                        <Stack
                                          onClick={() => {
                                            handleRevertToWaiting(selectedRow);
                                            closeOptions();
                                          }}
                                          sx={{
                                            cursor: "pointer",
                                            color: "#3D3D3D",
                                            display: "flex",
                                            "&:hover": {
                                              color: "#008080",
                                            },
                                          }}
                                        >
                                          <Typography>Revert to Waiting</Typography>
                                        </Stack>
                                      )}
                                    </Stack>
                                  </Paper>
                                </Popper>
                              </ClickAwayListener>
                            </>
                          )}
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={
                          columns.length +
                          (tableConfig.isAction ? 1 : 0) +
                          (tableActionButton?.length ? 1 : 0)
                        }
                        align="center"
                        className={Styles["no-data-found-cell"]}
                      >
                        <div className="d-flex flex-column w-100 justify-content-center align-items-center">
                          <img src={EmptyData_Image} alt="Empty Data Image" />
                          <Typography sx={{ m: 2, fontWeight: 700 }}>
                            Oops, It's Empty Here!
                          </Typography>
                          <Typography>
                            It seems there's nothing to display here
                          </Typography>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
            {isPaginationBar && (
              <Box className={Styles.PaginationContainer}>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 100]}
                  component="div"
                  count={isServerSide ? tableTotalCount : data?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Box>
            )}
          </Box>
        )}
      </TableContainer>
      <Dialog
        open={openPopup}
        onClose={() => setOpenPopup(false)}
        PaperProps={{
          style: {
            width: "100%",
            height: "fit-content",
            padding: 20,
          },
        }}
        fullWidth
        maxWidth="xs"
      >
        <Stack alignItems={"end"} justifyContent={"end"}>
          <CloseIcon
            className={Styles.Close_Icon}
            onClick={() => setOpenPopup(false)}
          />
        </Stack>
        <Stack alignItems={"center"}>
          <Stack padding={1}>
            <img
              src={ConfirmDelete_Icon}
              alt="ConfirmDelete_Icon"
              height={50}
            />
          </Stack>
          <Typography
            sx={{ fontSize: "20px", fontWeight: 700, color: "#3F3F46" }}
          >
            Are you sure ?
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              textAlign: "center",
              padding: "0 50px",
            }}
          >
            Are you sure you want to delete this item? This action cannot be
            undone.
          </Typography>
          <Stack direction={"row"} sx={{ marginTop: 2, gap: 2 }}>
            <Button
              onClick={() => setOpenPopup(false)}
              className={Styles.cancelButton}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                handleDelete(selectedRow);
                setOpenPopup(false);
              }}
              sx={{ color: "white !important" }}
              className={Styles.saveButton}
            >
              Confirm
            </Button>
          </Stack>
        </Stack>
      </Dialog>
    </div>
  );
};

export default MasterCommonTable;